import moment from 'moment';
import config from 'config';

const parseInt10 = x => parseInt(x, 10);

export function buildSearchParams(query, countries = []) {
  if (!query) {
    return {};
  }

  let {
    bikeTypes,
    pickUpCity,
    dropOfCity,
    begin: beginDateTime,
    end: endDateTime,
    age,
    ordering,
    code
  } = query;

  if (bikeTypes) {
    bikeTypes = bikeTypes.split(',')
      .map(parseInt10)
      .filter(Boolean);
  } else {
    bikeTypes = [];
  }

  pickUpCity = parseInt10(pickUpCity);
  dropOfCity = parseInt10(dropOfCity) || pickUpCity;

  const country = pickUpCity && countries.find((country) => (
    country.cities.find((city) => city.id === pickUpCity)
  ));

  // sync start/end time
  const dateNow = moment();

  if (beginDateTime) {
    beginDateTime = moment(beginDateTime).toDate();
  } else {
    beginDateTime = moment(dateNow).add(2, 'days').toDate();
  }

  if (endDateTime) {
    endDateTime = moment(endDateTime).toDate();
  } else {
    endDateTime = moment(dateNow).add(3, 'days').toDate();
  }

  return {
    bikeTypes,
    country: country && country.id,
    pickUpCity,
    dropOfCity,
    returnAtSameCity: pickUpCity === dropOfCity,
    beginDateTime,
    endDateTime,
    age,
    ordering,
    code
  };
}

const knownParams = [
  {
    name: 'bikeTypes',
    stringify: types => types.length ? types.join(',') : undefined,
    parse: typeIds => typeIds
      .split(',')
      .map(parseInt10)
      .filter(Boolean)
  },
  {
    name: 'beginDateTime',
    stringify: date => date.toISOString ? date.toISOString() : date,
    parse: dateStr => moment(dateStr).toDate(),
    alias: 'begin'
  },
  {
    name: 'endDateTime',
    stringify: date => date.toISOString ? date.toISOString() : date,
    parse: dateStr => moment(dateStr).toDate(),
    alias: 'end'
  },
  {
    name: 'pickUpCity',
    parse: parseInt10
  },
  {
    name: 'dropOfCity',
    parse: parseInt10
  },
  {
    name: 'age'
  },
  {
    name: 'ordering'
  },
  {
    name: 'x1'
  },
  {
    name: 'y1'
  },
  {
    name: 'x2'
  },
  {
    name: 'y2'
  },
  {
    name: 'company'
  },
  {
    name: 'count',
    parse: parseInt10
  },
  {
    name: 'extras',
    stringify: extras => extras.join(','),
    parse: extrasIds => extrasIds
      .split(',')
      .map(parseInt10)
      .filter(Boolean)
  },
  { name: 'sourceUrl', alias: 'source_url' },
  { name: 'sourceCompanyId', alias: 'source_company_id' }
];

export const buildApiParams = params => knownParams.reduce(
  (acc, { name, stringify, alias }) => {
    const value = params[name];
    const as = alias || name;

    if (value) {
      const stringified = stringify ? stringify(value) : value;

      if (stringified != null) {
        acc[as] = stringified;
      }
    }

    return acc;
  },
  {}
);

export const extractSearchParams = query => knownParams.reduce(
  (acc, { name, parse, alias }) => {
    const from = alias || name;
    const value = query[from];

    if (value) {
      acc[name] = parse ? parse(value) : value;
    }

    return acc;
  },
  {}
);


const copyTime = (to, from) => {
  const fromMoment = moment(from);
  const toMoment = moment(to);

  return toMoment
    .set('hour', fromMoment.get('hour'))
    .set('minute', fromMoment.get('minute'));
};

export const getEarliestBookingDate = () => moment()
  .startOf('hour')
  .add(1, 'hour')
  .add(config.minimalBookingOffset, 'hour')
  .toDate();

export const getEarliestDropDate = begin => moment(begin)
  .add(1, 'hour')
  .toDate();

/**
 * Two days ahead of now
 */
export const getDefaultBookingDate = (days = 2) => moment()
  .add(days, 'day')
  .startOf('day')
  .set('hour', 10)
  .toISOString();

/**
 * Three days ahead of now
 */
export const getDefaultDropDate = (days = 3) => moment()
  .add(days, 'day')
  .startOf('day')
  .set('hour', 10)
  .toISOString();

  /**
   * Creates new period using new begin date and old end date.
   * * limits begin by earliest booking date
   * * limits end by earliest drop date (moves 1 day ahead)
   * @param {Date} newBeginDate Begin date to set
   * @param {Object} currentPeriod Current period object to update
   * @return {Object} Period object containing beginDateTime and endDateTime
   */
export const updateBookingBeginDate = (newBeginDate, { endDateTime }) => {
  const earliestMoment = moment(getEarliestBookingDate());
  const newBeginMoment = earliestMoment.isBefore(newBeginDate)
    ? moment(newBeginDate)
    : earliestMoment;

  const oldEndMoment = moment(endDateTime);
  const earliestDropDate = getEarliestDropDate(newBeginMoment);
  const newEndMoment = oldEndMoment.isBefore(earliestDropDate)
    ? newBeginMoment.clone().add(1, 'day')
    : oldEndMoment;

  return {
    beginDateTime: newBeginMoment.toISOString(),
    endDateTime: newEndMoment.toISOString()
  };
};

export const updateBookingBeginDay = (newBeginDay, {endDateTime, beginDateTime}) => {
  const newBeginDate = copyTime(newBeginDay, beginDateTime);

  return updateBookingBeginDate(newBeginDate, { beginDateTime, endDateTime });
};

export const updateBookingEndDate = (newEndDate, { beginDateTime }) => {
  const possibleEndMoment = moment(newEndDate);
  const oldBeginMoment = moment(beginDateTime);

  const earliestDropMoment = moment(getEarliestDropDate(oldBeginMoment));

  if (possibleEndMoment.isAfter(earliestDropMoment)) {
    return {
      endDateTime: possibleEndMoment.toISOString(),
      beginDateTime: oldBeginMoment.toISOString()
    };
  }

  const possibleBeginMoment = possibleEndMoment.clone().subtract(1, 'day');
  const earliestBeginMoment = moment(getEarliestBookingDate());
  const newBeginMoment = possibleBeginMoment.isAfter(earliestBeginMoment)
    ? possibleBeginMoment
    : earliestBeginMoment;

  const newEndMoment = newBeginMoment.clone().add(1, 'day');

  return {
    beginDateTime: newBeginMoment.toISOString(),
    endDateTime: newEndMoment.toISOString()
  };
};

export const updateBookingEndDay = (newEndDay, {endDateTime, beginDateTime}) => {
  const newEndDate = copyTime(newEndDay, endDateTime);

  return updateBookingEndDate(newEndDate, { beginDateTime, endDateTime });
};



// WEBPACK FOOTER //
// ./src/utils/search.js